import React, { useEffect, useRef } from 'react';
import { Row, Col } from 'antd';
import ContentsOfCampaignHolder from '../../styles/contentsOfCampaignStyle';
import useModalState from '../../hooks/contentManager/useModalState';
import useResolutionLogic from '../../hooks/contentManager/useResolutionLogic';
import useContentOfCompaign from '../../hooks/contentManager/useContentOfCompaign';
import ContentFilters from './ContentFilters';
import ContentSlider from './ContentSlider';
import ResolutionSlider from './ResolutionSlider';
import PlaceholderSlider from './PlaceholderSlider';
import ContentsOfCampaignModals from './ContentsOfCampaignModals';

const WITH_CARD_IN_RELATION = 130;

function SliderNextArrow({ className, style, onClick }) {
  return (
    <div
      className={className}
      style={{ ...style, display: 'block', right: '-15px', top: '108px' }}
      onClick={onClick}
    />
  );
}

function SliderPrevArrow({ className, style, onClick }) {
  return (
    <div
      className={className}
      style={{ ...style, display: 'block', left: '-30px', top: '108px', zIndex: '1' }}
      onClick={onClick}
    />
  );
}

const ContentsOfCampaignComponent = props => {
  const {
    cart,
    setListOfContentsInCampaign,
    listOfContentsInCampaign,
    cleanListOfContentsInCampaign,
    setListResolutionWithContent,
    listResolutionWithContent,
    deleteContentFromCampaign,
    updateListOfContentsInCampaign,
    customContents,
    removeRelationFromDisplay,
    clearCustomContents,
    errors,
    groupedDisplays,
    groupName,
    selectedContent,
    toleranceResolutionInit,
    toleranceResolution,
    setToleranceResolution,
    isSuperUser,
    editCampaignActive,
    currentCampaign,
    applyRelationOnScreens,
    removeRelationsOnCampaign,
    filterByContent,
    updateCustomContents,
    isPaymentStatusApproved,
    updateImpressionsDisplay,
    fromModal,
    initialBackup,
    hasInitialBackup,
    setInitialBackup,
    restoreCampaignToOriginalState,
    creationType,
    getPlaceholders,
    placeholders,
    fetchingPlaceholders,
    setListOfPlaceholdersInCampaign,
    setListResolutionWithPlaceholders,
    reportSingleData
  } = props;

  // ── Modal state ────────────────────────────────────────────────
  const modalState = useModalState({ deleteContentFromCampaign });

  // ── Resolution logic ───────────────────────────────────────────
  const resolution = useResolutionLogic({
    cart,
    listOfContentsInCampaign,
    customContents,
    toleranceResolution,
    listResolutionWithContent,
    editCampaignActive,
    currentCampaign,
    creationType,
    placeholders,
    isSuperUser,
    isPaymentStatusApproved,
    toleranceResolutionInit,
    initialBackup,
    hasInitialBackup,
    groupedDisplays,
    filterByContent,
    selectedContent,
    fromModal,
    groupName,
    setListResolutionWithContent,
    setListOfContentsInCampaign,
    updateCustomContents,
    applyRelationOnScreens,
    setToleranceResolution,
    setInitialBackup,
    setListOfPlaceholdersInCampaign,
    setListResolutionWithPlaceholders,
    getPlaceholders,
    restoreCampaignToOriginalState,
    clearCustomContents,
    cleanListOfContentsInCampaign,
    deletingContentFromLibrary: modalState.deletingContentFromLibrary,
    setDeletingContentFromLibrary: modalState.setDeletingContentFromLibrary,
    reportSingleData
  });

  // ── Content actions ────────────────────────────────────────────
  const contentActions = useContentOfCompaign({
    listOfContentsInCampaign,
    customContents,
    initialBackup,
    editCampaignActive,
    hasInitialBackup,
    groupedDisplays,
    cart,
    listResolutionWithContent,
    setListResolutionWithContent,
    updateListOfContentsInCampaign,
    updateCustomContents,
    applyRelationOnScreens,
    restoreCampaignToOriginalState,
    clearCustomContents,
    cleanListOfContentsInCampaign,
    showModalCleanContent: modalState.showModalCleanContent,
    setResolutionFilterIndex: resolution.setResolutionFilterIndex,
    setVersionFilter: resolution.setVersionFilter,
    contentToCloneName: modalState.contentToCloneName,
    cancelModalCloneNameContent: modalState.cancelModalCloneNameContent
  });

  // ── Slider settings ────────────────────────────────────────────
  const settingsSlider = {
    infinite: false,
    speed: 600,
    slidesToShow: 3,
    slidesToScroll: 2,
    nextArrow: <SliderNextArrow />,
    prevArrow: <SliderPrevArrow />,
    variableWidth: true,
    adaptiveHeight: false,
    swipeToSlide: true,
    afterChange: current => resolution.setCurrentStick(current),
    responsive: [
      { breakpoint: 600, settings: { slidesToShow: 2, slidesToScroll: 1 } },
      { breakpoint: 480, settings: { slidesToShow: 1, slidesToScroll: 1 } }
    ]
  };

  const prevContentsRef = useRef(listOfContentsInCampaign);
  useEffect(() => {
    const prevContents = prevContentsRef.current;
    prevContentsRef.current = listOfContentsInCampaign;
    if (!editCampaignActive || !isPaymentStatusApproved) return;
    if (listOfContentsInCampaign.length <= prevContents.length) return;
    const prevKeys = new Set(prevContents.map(c => `${c.id}_${c.content_version_name}`));
    const newItems = listOfContentsInCampaign.filter(
      c => !prevKeys.has(`${c.id}_${c.content_version_name}`)
    );
    if (newItems.some(item => item._isFromReplacement)) return;
    const timer = setTimeout(() => {
      resolution.applyTolerance();
    }, 500);
    return () => clearTimeout(timer);
  }, [listOfContentsInCampaign.length]);

  const isProposalMode = !editCampaignActive && creationType === 'makeProposal';

  const filterVersions = [
    ...new Set(['V1', ...listOfContentsInCampaign.map(c => c.content_version_name || 'V1')])
  ].sort((a, b) => {
    const sortKey = v => {
      const match = /^V(\d+)$/i.exec(v);
      return match ? parseInt(match[1], 10) : Infinity;
    };
    return sortKey(a) - sortKey(b);
  });

  return (
    <ContentsOfCampaignHolder withCardInRelation={WITH_CARD_IN_RELATION}>
      {creationType === 'createCampaign' || editCampaignActive ? (
        <Row>
          {/* ── Filters row (only outside grouped-display modal) ── */}
          {!groupedDisplays && (
            <ContentFilters
              editCampaignActive={editCampaignActive}
              currentCampaign={currentCampaign}
              versionFilter={resolution.versionFilter}
              setVersionFilter={resolution.setVersionFilter}
              resolutionFilterIndex={resolution.resolutionFilterIndex}
              setResolutionFilterIndex={resolution.setResolutionFilterIndex}
              listContentResolutions={resolution.listContentResolutions}
              versions={filterVersions}
              clearProgressDisabled={contentActions.clearProgressDisabled}
              showModalCleanContent={modalState.showModalCleanContent}
            />
          )}

          {/* ── Top slider: content cards ── */}
          <ContentSlider
            listOfContentsInCampaignFiltered={resolution.listOfContentsInCampaignFiltered}
            groupedDisplays={groupedDisplays}
            isPaymentStatusApproved={isPaymentStatusApproved}
            editCampaignActive={editCampaignActive}
            currentCampaign={currentCampaign}
            settingsSlider={settingsSlider}
            customContents={customContents}
            handleContent={contentActions.handleContent}
            showModalContent={modalState.showModalContent}
            showModalDeleteContentRelation={modalState.showModalDeleteContentRelation}
            showModalCloneNameContent={modalState.showModalCloneNameContent}
            haveContentInRelation={resolution.haveContentInRelation}
            getTooltipAndCompatibility={resolution.getTooltipAndCompatibility}
            openNotification={resolution.openNotification}
            checkAddInRelation={resolution.checkAddInRelation}
          />

          {/* ── Bottom slider: required resolution cards or placeholders ── */}
          <Col xs={24}>
            {!isProposalMode ? (
              <ResolutionSlider
                listResolution={resolution.listResolution}
                groupedDisplays={groupedDisplays}
                filterByContent={filterByContent}
                hasDifferentResolution={resolution.hasDifferentResolution}
                resolutionTitle={resolution.resolutionTitle}
                resolutionSubtitle={resolution.resolutionSubtitle}
                errors={errors}
                toleranceResolution={toleranceResolution}
                toleranceResolutionInit={toleranceResolutionInit}
                isSuperUser={isSuperUser}
                isPaymentStatusApproved={isPaymentStatusApproved}
                editCampaignActive={editCampaignActive}
                currentCampaign={currentCampaign}
                customContents={customContents}
                listResolutionWithContent={listResolutionWithContent}
                setListResolutionWithContent={setListResolutionWithContent}
                removeRelationFromDisplay={removeRelationFromDisplay}
                listOfContentsInCampaign={listOfContentsInCampaign}
                setListOfContentsInCampaign={setListOfContentsInCampaign}
                updateImpressionsDisplay={updateImpressionsDisplay}
                removeRelationsOnCampaign={removeRelationsOnCampaign}
                showModalDeleteContentRelation={modalState.showModalDeleteContentRelation}
                showModalContent={modalState.showModalContent}
                isTheLastOfTheRelationship={resolution.isTheLastOfTheRelationship}
                setTolerance={resolution.setTolerance}
                applyTolerance={resolution.applyTolerance}
                withCardInRelation={WITH_CARD_IN_RELATION}
                fromModal={fromModal}
              />
            ) : (
              <PlaceholderSlider
                listResolution={resolution.listResolution}
                fetchingPlaceholders={fetchingPlaceholders}
                placeholders={placeholders}
              />
            )}
          </Col>
        </Row>
      ) : (
        /* ── makeProposal mode (nueva propuesta, sin editar campaña) ── */
        <PlaceholderSlider
          listResolution={resolution.listResolution}
          fetchingPlaceholders={fetchingPlaceholders}
          placeholders={placeholders}
        />
      )}

      <ContentsOfCampaignModals
        visibleModalDeleteContentRelation={modalState.visibleModalDeleteContentRelation}
        showModalDeleteContentRelation={modalState.showModalDeleteContentRelation}
        deleteContentOfListRelation={modalState.deleteContentOfListRelation}
        visibleModalCleanContent={modalState.visibleModalCleanContent}
        showModalCleanContent={modalState.showModalCleanContent}
        cleanProgress={contentActions.cleanProgress}
        visibleModalCloneNameContent={modalState.visibleModalCloneNameContent}
        cancelModalCloneNameContent={modalState.cancelModalCloneNameContent}
        cloneNameToAllVersions={contentActions.cloneNameToAllVersions}
        visibleModalContent={modalState.visibleModalContent}
        cancelModalContent={modalState.cancelModalContent}
        contentModal={modalState.contentModal}
      />
    </ContentsOfCampaignHolder>
  );
};

export default ContentsOfCampaignComponent;
